/**
 * The human readable references to the type values we get from our API
 */
const projectTypes = {
  initialBuild: 'Projects::InitialEmployerBuild',
  coverageBuild: 'Projects::CoverageBuild',
  policyAudit: 'Projects::PolicyAudit',
  goToMarket: 'Projects::RfpPlacementEvent',
  renewal: 'Projects::RenewalPlacementEvent',
  isBuildType: (type) => (
    [projectTypes.initialBuild, projectTypes.coverageBuild].includes(type)
  ),
};

/**
 * Reusable Data Utils
 *
 * @vuedoc
 * @exports ReusableData
 * @category Utils
 */
export default {
  /**
   * Return a list of Months
   *
   * @returns {Array}
   */
  getMonths() {
    const months = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];
    return months;
  },

  /**
   * Whether a subtype should default to compund
   *
   * @param {string} subtypeLabel
   * @returns {string}
   */
  getCompoundBySubtype(subtypeLabel) {
    let compound = null;
    switch (subtypeLabel) {
      case 'spouse':
      case 'child':
      case 'core':
      case 'buyup':
      case 'male':
      case 'female':
      case 'employee':
      case 'family':
        compound = true;
        break;
      case 'non_smoker':
      case 'smoker':
        compound = false;
        break;
      default:
        break;
    }
    return compound;
  },

  /**
   * What subtypes to show based on a products name
   *
   * @param {string} productTypeName
   * @returns {Array}
   */
  getSubtypeLabels(productTypeName) {
    const smokerNonSmoker = { label: 'Non-Smoker/Smoker', subtypes: ['non_smoker', 'smoker'] };
    const spouseChild = { label: 'Spouse/Child', subtypes: ['spouse', 'child'] };
    const employeeFamily = { label: 'Employee/Family', subtypes: ['employee', 'family'] };
    const coreBuyup = { label: 'Core/Buy-Up', subtypes: ['core', 'buyup'] };
    const maleFemale = { label: 'Male/Female', subtypes: ['male', 'female'] };
    const subtypesByProduct = {
      'Basic Life / AD&D': [],
      'Basic Dependent Life': [spouseChild],
      'Optional Life': [employeeFamily, smokerNonSmoker, maleFemale],
      'Optional AD&D': [employeeFamily, smokerNonSmoker],
      'Advice To Pay': [],
      'Statutory Plan - CA,HI,NJ,PR,RI': [coreBuyup, maleFemale],
      'Short Term Disability': [coreBuyup, maleFemale],
      'NY DBL': [maleFemale],
      'Long Term Disability': [coreBuyup],
    };
    return subtypesByProduct[productTypeName];
  },

  /**
   * What rate basis options to show based on a products int
   *
   * @param {number} productTypeId
   * @returns {Array}
   */
  getRateBasisOptions(productTypeId) {
    const per10 = { label: 'Per $10', value: 10 };
    const per25 = { label: 'Per $25', value: 25 };
    const per50 = { label: 'Per $50', value: 50 };
    const per75 = { label: 'Per $75', value: 75 };
    const per100 = { label: 'Per $100', value: 100 };
    const per1000 = { label: 'Per $1,000', value: 1000 };
    const per2000 = { label: 'Per $2,000', value: 2000 };
    const per5000 = { label: 'Per $5,000', value: 5000 };
    const per10000 = { label: 'Per $10,000', value: 10000 };
    const per15000 = { label: 'Per $15,000', value: 15000 };
    const per20000 = { label: 'Per $20,000', value: 20000 };
    const per30000 = { label: 'Per $30,000', value: 30000 };
    const perUnit = { label: 'Per Unit', value: 1 };
    const perEmployeeMonth = { label: 'Per Employee Per Month', value: 1 };
    // This is an array of the objects below so the product types using this in the rateBasisOptionsByProductTypeId object should not be in an array or they should use a spread operator to join them to another array.
    const criticalAccidentAndHospital = [
      perEmployeeMonth,
      per25,
      per50,
      per75,
      per1000,
      per2000,
      per5000,
      per10000,
    ];
    const rateBasisOptionsByProductTypeId = {
      1: [per10, per100, perEmployeeMonth], // short term disability
      2: [per1000, perEmployeeMonth], // basic life/AD&D
      3: [per1000, perUnit], // Basic Dependent Life
      4: [per1000, per2000, per5000, per10000, perUnit], // Voluntary Life
      5: [per1000, per2000, per5000, per10000, perUnit], // Optional AD&D
      7: [per100, perEmployeeMonth], // Long Term Disability
      8: [perEmployeeMonth], // Advice To Pay
      9: [per10, per100, perEmployeeMonth], // NY DBL
      11: [per10, per100, perEmployeeMonth], // NJ TDB,
      12: [perEmployeeMonth], // absence management
      13: [per10, per100, perEmployeeMonth], // HI TDI
      14: [perEmployeeMonth], // Dental
      15: [perEmployeeMonth], // Vision
      16: [...criticalAccidentAndHospital, per15000, per20000, per30000], // Critical Illness
      17: criticalAccidentAndHospital, // Accident
      18: criticalAccidentAndHospital, // Hospital Indemnity
      19: [perEmployeeMonth], // Stop Loss
      21: [per1000, per2000, per5000, per10000, perUnit], // Voluntary Life / AD&D
    };
    return rateBasisOptionsByProductTypeId[productTypeId];
  },

  /**
   * labels to show for age banded values
   *
   * @returns {Array}
   */
  getAgeBandedValues() {
    const labels = [
      'age_0_19',
      'age_20_24',
      'age_25_29',
      'age_30_34',
      'age_35_39',
      'age_40_44',
      'age_45_49',
      'age_50_54',
      'age_55_59',
      'age_60_64',
      'age_65_69',
      'age_70_74',
      'age_75_79',
      'age_80_plus',
    ];
    return labels;
  },

  /**
   * An array of file types we allow to be uploaded
   *
   * @returns {Array}
   */
  getAcceptableUploadFileTypes() {
    return [
      'application/pdf',
      'application/msword',
      'application/rtf',
      'application/vnd.ms-excel',
      'application/vnd.ms-excel.sheet.binary.macroEnabled.12',
      'application/vnd.ms-excel.sheet.macroEnabled.12',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/rtf',
    ];
  },

  /**
   * An array of file extensions we allow to be uploaded
   *
   * @returns {Array}
   */
  getAcceptableUploadFileExtensions() {
    return [
      '.doc',
      '.docx',
      '.pdf',
      '.rtf',
      '.txt',
      '.xls',
      '.xlsb',
      '.xlsm',
      '.xlsx',
    ];
  },
  projectTypes,
};
